/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { ref } from "vue";

/**
 * Composable for grid validation logic
 * Handles grid bounds validation and overlap detection
 */
export function useGridValidation(gridSettings, blocks, toast, t) {
  const previousGridSettings = ref({ ...gridSettings.value });

  /**
   * Check if a block overlaps with any other block
   */
  const hasOverlap = (block) => {
    return blocks.value.some((otherBlock) => {
      if (otherBlock.id === block.id) {
        return false;
      }

      // Check if rectangles overlap
      const xOverlap =
        block.gridPosition.colStart < otherBlock.gridPosition.colEnd &&
        block.gridPosition.colEnd > otherBlock.gridPosition.colStart;

      const yOverlap =
        block.gridPosition.rowStart < otherBlock.gridPosition.rowEnd &&
        block.gridPosition.rowEnd > otherBlock.gridPosition.rowStart;

      return xOverlap && yOverlap;
    });
  };

  /**
   * Validate and automatically adjust grid if blocks exceed bounds
   */
  const validateAndAdjustGrid = (emitUpdate) => {
    if (blocks.value.length === 0) {
      return;
    }

    const maxColUsed = Math.max(
      ...blocks.value.map((b) => b.gridPosition.colEnd - 1),
      0,
    );
    const maxRowUsed = Math.max(
      ...blocks.value.map((b) => b.gridPosition.rowEnd - 1),
      0,
    );

    let adjusted = false;

    if (gridSettings.value.columns < maxColUsed) {
      gridSettings.value.columns = maxColUsed;
      adjusted = true;
    }

    if (gridSettings.value.rows < maxRowUsed) {
      gridSettings.value.rows = maxRowUsed;
      adjusted = true;
    }

    if (adjusted) {
      previousGridSettings.value = { ...gridSettings.value };
      toast.add({
        severity: "warn",
        summary: t("dashboardAdmin.gridAutoAdjusted"),
        detail: t("dashboardAdmin.gridAutoAdjustedDetail", {
          columns: gridSettings.value.columns,
          rows: gridSettings.value.rows,
        }),
        life: 5000,
      });
      if (emitUpdate) {
        emitUpdate();
      }
    }
  };

  /**
   * Validate grid settings changes and revert if invalid
   */
  const updateGridSettings = (emitUpdate) => {
    // Check if any block exceeds the new grid dimensions
    const maxColUsed = Math.max(
      ...blocks.value.map((b) => b.gridPosition.colEnd - 1),
      0,
    );
    const maxRowUsed = Math.max(
      ...blocks.value.map((b) => b.gridPosition.rowEnd - 1),
      0,
    );

    if (
      gridSettings.value.columns < maxColUsed ||
      gridSettings.value.rows < maxRowUsed
    ) {
      // Find which block is causing the issue
      const problematicBlocks = blocks.value.filter(
        (b) =>
          b.gridPosition.colEnd - 1 > gridSettings.value.columns ||
          b.gridPosition.rowEnd - 1 > gridSettings.value.rows,
      );

      const blockNames = problematicBlocks
        .map((b) => b.title || b.type)
        .join(", ");

      toast.add({
        severity: "error",
        summary: t("error"),
        detail: t("dashboardAdmin.gridTooSmall", {
          blocks: blockNames,
          maxCol: maxColUsed,
          maxRow: maxRowUsed,
        }),
        life: 5000,
      });

      // Revert to previous settings
      gridSettings.value.columns = previousGridSettings.value.columns;
      gridSettings.value.rows = previousGridSettings.value.rows;
      return;
    }

    // If validation passed, save the new settings
    previousGridSettings.value = { ...gridSettings.value };
    if (emitUpdate) {
      emitUpdate();
    }
  };

  return {
    hasOverlap,
    validateAndAdjustGrid,
    updateGridSettings,
    previousGridSettings,
  };
}
